import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { NotificationsEchoBridge } from '~/interfaces/notificationsEcho'
import { ToastType } from '~/enums'
import type { NotificationInboxItem, RealtimeNotificationPayload } from '~/services/api'
import { notificationCloudEvent } from '~/services/api/notification/notificationCloudEvent'
import { useLogger } from '~/composables/useLogger'
import { resolveNotificationDisplay } from '~/composables/resolveNotificationDisplay'
import { identityGeneration } from '~/utils/identityGeneration'
import { Normalization } from '~/utils/normalization'
import { notificationsConnectionTracking } from '~/utils/notificationsConnectionTracking'
import { NotificationInboxDtoSchema } from '~/services/api/generated'
import { useNotificationsStore } from '~/stores/useNotificationsStore'
import { useToast } from '~/composables/useToast'

const { isNotificationCloudEvent, unwrapNotificationCloudEvent } = notificationCloudEvent

const isRecord = Normalization.isRecord

const buildFallbackNotificationId = () =>
  `live-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const TOAST_TYPE_BY_SEVERITY: Record<string, ToastType> = {
  danger: ToastType.ERROR,
  error: ToastType.ERROR,
  info: ToastType.NEUTRAL,
  neutral: ToastType.NEUTRAL,
  success: ToastType.SUCCESS,
  warning: ToastType.WARNING
}

const getToastType = (severity: string | null): ToastType =>
  (severity != null && severity !== '' ? TOAST_TYPE_BY_SEVERITY[severity] : undefined) ??
  ToastType.NEUTRAL

// Live pushes are template-driven and omit the inbox-only is_read/read_at fields.
const RealtimeNotificationItemSchema = NotificationInboxDtoSchema.omit({
  is_read: true,
  read_at: true
})

const normalizeRealtimeNotification = (
  payload: Partial<RealtimeNotificationPayload>
): NotificationInboxItem | null => {
  const candidate = {
    id: Normalization.stringValue(payload.id, null) ?? buildFallbackNotificationId(),
    type: payload.type ?? 'notification',
    action_url: payload.action_url ?? '',
    template_code: payload.template_code ?? null,
    title: payload.title ?? null,
    body: payload.body ?? null,
    action_label: payload.action_label ?? null,
    params: payload.params ?? {},
    reason: payload.reason ?? null,
    action: payload.action ?? null,
    entity_type: payload.entity_type ?? '',
    entity_id: payload.entity_id ?? null,
    meta: payload.meta ?? {},
    severity: payload.severity ?? 'info',
    schema_version: payload.schema_version ?? 1,
    expires_at: payload.expires_at ?? null,
    created_at: payload.created_at ?? new Date().toISOString()
  }

  const parsed = RealtimeNotificationItemSchema.safeParse(candidate)
  if (!parsed.success) return null

  return { ...parsed.data, is_read: false, read_at: null }
}

type ToastShow = ReturnType<typeof useToast>['show']
type Translate = ReturnType<typeof useI18n>['t']
type NotificationsStore = ReturnType<typeof useNotificationsStore>
type Logger = ReturnType<typeof useLogger>

interface RealtimeNotificationContext {
  logger: Logger
  notificationsStore: NotificationsStore
  show: ToastShow
  t: Translate
  openNotification: (notification: NotificationInboxItem) => Promise<void>
}

const hasActionUrl = (notification: NotificationInboxItem): boolean =>
  notification.action_url != null && notification.action_url !== ''

const showRealtimeToast = (
  notification: NotificationInboxItem,
  { show, t, openNotification }: RealtimeNotificationContext
) => {
  const display = resolveNotificationDisplay(notification, t)
  show(getToastType(notification.severity), display.title, {
    duration: 8500,
    ...(display.body !== '' ? { description: display.body } : {}),
    ...(hasActionUrl(notification)
      ? {
          action: {
            label: display.actionLabel !== '' ? display.actionLabel : 'View',
            onClick: () => {
              void openNotification(notification)
            }
          }
        }
      : {})
  })
}

const handleRealtimeNotification = (payload: unknown, context: RealtimeNotificationContext) => {
  const { logger, notificationsStore } = context

  if (!isRecord(payload) || !isNotificationCloudEvent(payload)) {
    logger.warn('subscribe', {
      message: 'Ignored malformed realtime notification payload',
      payload
    })
    return
  }

  const notification = normalizeRealtimeNotification(unwrapNotificationCloudEvent(payload))
  if (notification === null) {
    logger.warn('subscribe', {
      message: 'Ignored realtime notification payload that failed schema validation',
      payload
    })
    return
  }

  const wasInserted = notificationsStore.prependItem(notification, payload.id)
  if (!wasInserted) return

  notificationsStore.incrementUnreadCount()
  showRealtimeToast(notification, context)
}

const getEchoBridge = (): NotificationsEchoBridge | null => {
  const nuxtApp = useNuxtApp()

  return nuxtApp.$notificationsEcho ?? null
}

interface NotificationsRealtimeOptions {
  isAuthenticated: Readonly<Ref<boolean>>
  broadcastChannelIdentifier: Readonly<Ref<string | null>>
  openNotification: (notification: NotificationInboxItem) => Promise<void>
}

/** Owns the Echo private-channel subscription that pushes live notifications into the store. */
export const useNotificationsRealtime = ({
  isAuthenticated,
  broadcastChannelIdentifier,
  openNotification
}: NotificationsRealtimeOptions) => {
  const logger = useLogger('useNotificationsRealtime')
  const notificationsStore = useNotificationsStore()
  const { show } = useToast()
  const { t } = useI18n()

  const leaveChannel = () => {
    if (import.meta.server) {
      return
    }

    const echo = getEchoBridge()?.getInstance()
    const channelName = notificationsStore.subscribedChannelName

    if (echo != null && channelName != null && channelName !== '') {
      echo.leave(channelName)
    }

    notificationsConnectionTracking.stop()
    notificationsStore.setSubscribedChannelName(null)
    notificationsStore.setConnectionStatus('disconnected')
  }

  // An explicit unsubscribe also cancels a subscribe still being installed.
  const unsubscribe = async () => {
    notificationsConnectionTracking.invalidate()
    leaveChannel()
  }

  const subscribe = async () => {
    if (
      import.meta.server ||
      !isAuthenticated.value ||
      broadcastChannelIdentifier.value == null ||
      broadcastChannelIdentifier.value === ''
    ) {
      return false
    }

    const echoBridge = getEchoBridge()

    if (echoBridge?.isConfigured !== true) {
      notificationsStore.setConnectionStatus('failed')
      return false
    }

    const echo = echoBridge.ensureConnected()

    if (!echo) {
      notificationsStore.setConnectionStatus('failed')
      return false
    }

    const channelName = `App.Models.User.${broadcastChannelIdentifier.value}`

    if (notificationsStore.subscribedChannelName === channelName) {
      return true
    }

    const generation = identityGeneration.current()
    notificationsConnectionTracking.invalidate()
    const epoch = notificationsConnectionTracking.epoch()
    const isCurrent = () =>
      identityGeneration.isCurrent(generation) && notificationsConnectionTracking.isCurrent(epoch)
    leaveChannel()

    notificationsStore.setSubscribedChannelName(channelName)
    notificationsStore.setConnectionStatus(echo.connector.connectionStatus())
    notificationsConnectionTracking.start(() =>
      echo.connector.onConnectionChange((status) => {
        if (!isCurrent()) return

        notificationsStore.setConnectionStatus(status)
      })
    )

    echo
      .private(channelName)
      .error(() => {
        if (!isCurrent()) return

        notificationsStore.setConnectionStatus('failed')
      })
      .notification((payload: unknown) => {
        if (!isCurrent()) return

        handleRealtimeNotification(payload, {
          logger,
          notificationsStore,
          show,
          t,
          openNotification
        })
      })

    return true
  }

  return { subscribe, unsubscribe }
}
